import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'

import BaseModal from '../BaseModal2'
import SearchUser from '../../ChatModals/SearchUser'
import Button from '../../../atoms/Button2'
import HorizontalToggle from '../../../atoms/HorizontalToggle'
import TextRow from '../BaseModal/TextRow'
import GroupChat from './GroupChat'

import {
  postStack,
  removeFromStack,
  removeParticipant
} from '../../../../lib/api'
import { useSocket } from '../../../../providers/SocketProvider'
import { useConversationScroll } from '../../../../providers/ConversationScrollProvider'
import { useQueueState } from '../../../../providers/QueueStateProvider'
import { useAgent } from '../../../../providers/AgentProvider'
import {
  removeDM,
  selectDMs,
  setChatName,
  setChatType
} from '../../../../slices/dms'
import { AddErrorAlert } from '../../../../slices/alert'
import useStyles from './styles'

const OPTIONS = ['Private Chat', 'Group Chat']

const UserSearchModal = ({
  open,
  onClose,
  convo,
  selected,
  setSelected,
  setRef,
  ...props
}) => {
  const dispatch = useAppDispatch()

  const {
    peerChat: {
      allGroupChats,
      addAgent,
      formatGroupChat,
      setNewPush,
      setAllGroupChats
    }
  } = useSocket()
  const scroll = useConversationScroll()
  const { dms, chatType } = useAppSelector(selectDMs)
  const { handleSelect: selectConvo, setScrollTo } = useQueueState()
  const { currentUser } = useAgent()

  const isGroupChat = React.useMemo(() => chatType === 'Group Chat', [chatType])

  const classes = useStyles()

  const [users, setUsers] = React.useState([])
  const [name, setName] = React.useState('')

  const otherOpenConvos = React.useMemo(() => {
    if (!open) {
      return []
    }

    const currentGroupChats = Object.entries(dms)
      .filter(([_, val]) => val.isGroup)
      .map(([key, _]) => key)

    const otherOpen = []

    Object.entries(allGroupChats).forEach(([key, convo]) => {
      if (!currentGroupChats.includes(key)) {
        otherOpen.push(convo)
      }
    })

    const result = otherOpen.sort(
      (a, b) =>
        +new Date(b.latest_activity[0]?.stamp ?? b.latest_activity[0]?.date) -
        +new Date(a.latest_activity[0]?.stamp ?? a.latest_activity[0]?.date)
    )
    if (users.length > 0) {
      return result.filter(r =>
        users.some(u => r.userIds.some(uids => uids === u.id))
      )
    }
    return result
  }, [open, dms, allGroupChats, users])

  // selecting user from chip
  const handleUserSelect = React.useCallback(user => {
    let mounted = true

    if (mounted) {
      setUsers(user)
    }

    return () => (mounted = false)
  }, [])

  // adding user after button clicked and closes modal
  const handleAddUser = React.useCallback(() => {
    let uniqueIds = Array.from(new Set(users.map(u => u.id))).map(id => {
      return users.find(u => u.id === id)
    })

    if (isGroupChat) {
      uniqueIds = uniqueIds.filter(x => x !== currentUser()?.id)
    }

    setSelected(uniqueIds)
    setRef.current = false

    // check number of users to create group chat / peer chat
    if (dms[users[0]?.id]) {
      onClose()
    } else {
      // add user that has been deleted
      if (users.length === 1 && !isGroupChat) {
        addAgent(users[0])
        if (scroll) {
          scroll.triggerScroll()
        }
        onClose()
      }
    }
    onClose()
    setName('')
  }, [
    users,
    setSelected,
    onClose,
    addAgent,
    scroll,
    dms,
    setRef,
    isGroupChat,
    currentUser
  ])

  React.useEffect(() => {
    if (users.length > 1) {
      dispatch(setChatType('Group Chat'))
    }
  }, [users, dispatch])

  const handleAddName = React.useCallback(() => {
    dispatch(setChatName(name))
  }, [name, dispatch])

  const handleSelect = React.useCallback(
    id => () => {
      onClose()
      const convo = allGroupChats[id]
      // Adds to queue
      formatGroupChat(convo).then(() => {
        // Needed for accurate notification counts
        setNewPush(old => [...old, id])

        // Needed to save convo in queue
        const payload = {
          id,
          type: 'session'
        }
        postStack(payload, 'default').catch(err => {
          console.error('Error saving in stack', err)
        })

        // Select in queue
        selectConvo(id)

        // Scroll to this convo
        setScrollTo(id)
      })
    },
    [
      onClose,
      allGroupChats,
      formatGroupChat,
      setNewPush,
      selectConvo,
      setScrollTo
    ]
  )

  const handleLeaveConvo = React.useCallback(
    async id => {
      // send part message via socket
      try {
        await removeParticipant(id, currentUser()?.id)
        dispatch(removeDM(id))
        await removeFromStack(id, 'default')
        setAllGroupChats(old => {
          const copy = { ...old }
          delete copy[id]
          return copy
        })
      } catch (err) {
        dispatch(
          AddErrorAlert({ err: { message: 'Could not leave conversation.' } })
        )
        console.error('Error leaving convo', err)
      }
    },
    [currentUser, dispatch, setAllGroupChats]
  )

  return (
    <>
      <BaseModal
        open={open}
        onClose={() => {
          setName('')
          dispatch(setChatType('Private Chat'))
          onClose()
        }}
        title='Send Message To'
        PaperProps={{ style: { top: '53%', width: '38%' } }}
        {...props}
      >
        <HorizontalToggle
          name='chat_type'
          options={OPTIONS}
          selected={chatType}
          onChange={val => dispatch(setChatType(val))}
        />

        {isGroupChat && (
          <TextRow title={`Name this conversation:`}>
            <input
              value={name}
              placeholder='Optional'
              onChange={e => setName(e.target.value)}
              className={classes.input}
              maxLength={75}
              onBlur={() => handleAddName()}
            />
          </TextRow>
        )}

        <SearchUser onUserFound={handleUserSelect} peerChat={isGroupChat} />

        <div className={classes.submit}>
          <Button
            onClick={() => handleAddUser()}
            disabled={!users.length}
            className={classes.endButton}
          >
            {isGroupChat ? 'Create group chat' : 'Go to private chat'}
          </Button>
        </div>

        {otherOpenConvos.length > 0 && isGroupChat && (
          <>
            <div className={classes.title}>Hidden Group Chats</div>

            <div id='userSearchModalConvos' className={classes.convoList}>
              {otherOpenConvos.map(chat => (
                <GroupChat
                  key={chat.id}
                  chat={chat}
                  onLeave={() => handleLeaveConvo(chat.id)}
                  onSelect={handleSelect(chat.id)}
                />
              ))}
            </div>
          </>
        )}
      </BaseModal>
    </>
  )
}

export default UserSearchModal
